import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Modal from '../../../../components/Modal'
import Loader from '../../../../components/Loader'
import Toast from '../../../../components/Toast/Toast'
import {
  uploadCourseVideo,
  updateCourseVideo,
} from '../../../../services/Courses'
import { TAGS } from '../../../../services/Tags'
import { globalBootstrap } from '../../../..'

export const VIDEO_FORM_MODAL_ID = 'course-video-form-modal'

// Lee metadata del archivo de video (duración y alto) usando un <video> oculto,
// para pre-rellenar los valores de preview sin subir el archivo al servidor.
const extractVideoPreviewMetadata = (file) =>
  new Promise((resolve, reject) => {
    const videoEl = document.createElement('video')
    videoEl.preload = 'metadata'
    videoEl.muted = true
    const objectUrl = URL.createObjectURL(file)

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl)
      videoEl.removeAttribute('src')
      videoEl.load()
    }

    videoEl.onloadedmetadata = () => {
      const duration = videoEl.duration || 0
      const previewSeconds = Math.min(10, Math.max(5, Math.floor(duration) || 5))
      resolve({
        previewStart: '0',
        previewSeconds: String(previewSeconds),
        previewHeight: String(videoEl.videoHeight || ''),
      })
      cleanup()
    }

    videoEl.onerror = () => {
      cleanup()
      reject(new Error('No se pudo leer el video'))
    }

    videoEl.src = objectUrl
  })

// Modal de subir/editar video de un curso. Si `video` viene con id → modo edición.
const VideoFormModal = ({ courseId, video = null, onClose = () => {} }) => {
  const queryClient = useQueryClient()
  const fileInputRef = useRef(null)
  const isEdit = Boolean(video?.id)

  const [file, setFile] = useState(null)
  const [pdfFile, setPdfFile] = useState(null)
  const pdfInputRef = useRef(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [position, setPosition] = useState('')
  const [previewSeconds, setPreviewSeconds] = useState('')
  const [previewStart, setPreviewStart] = useState('')
  const [previewHeight, setPreviewHeight] = useState('')

  const resetForm = () => {
    setTitle(video?.title || '')
    setDescription(video?.description || '')
    setPosition(video?.position != null ? String(video.position) : '')
    setFile(null)
    setPdfFile(null)
    setPreviewSeconds(video?.previewSeconds || '')
    setPreviewStart(video?.previewStart || '')
    setPreviewHeight(video?.previewHeight || '')
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (pdfInputRef.current) pdfInputRef.current.value = ''
  }

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0] || null
    setFile(selected)
    if (!selected) return

    try {
      const metadata = await extractVideoPreviewMetadata(selected)
      setPreviewStart(metadata.previewStart)
      setPreviewSeconds(metadata.previewSeconds)
      setPreviewHeight(metadata.previewHeight)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    resetForm()
  }, [video])

  const closeModal = () => {
    globalBootstrap.Modal.getOrCreateInstance(`#${VIDEO_FORM_MODAL_ID}`).hide()
    onClose()
  }

  const handleSuccess = ({ response, res }, successMsg) => {
    if (!response?.ok) {
      Toast({
        icon: 'error',
        title: res?.msg || res?.error || 'No se pudo guardar el video',
      })
      return
    }
    queryClient.invalidateQueries([TAGS.course_videos, courseId])
    Toast({ title: successMsg })
    resetForm()
    closeModal()
  }

  const { mutate: upload, isLoading: isUploading } = useMutation(
    uploadCourseVideo,
    {
      onSuccess: (data) => handleSuccess(data, 'Video subido correctamente'),
      onError: () =>
        Toast({ icon: 'error', title: 'No se pudo subir el video' }),
    }
  )

  const { mutate: update, isLoading: isUpdating } = useMutation(
    updateCourseVideo,
    {
      onSuccess: (data) => handleSuccess(data, 'Video actualizado correctamente'),
      onError: () =>
        Toast({ icon: 'error', title: 'No se pudo actualizar el video' }),
    }
  )

  const isLoading = isUploading || isUpdating

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!title.trim() || !position.toString().trim()) {
      Toast({ icon: 'error', title: 'Completa título y posición' })
      return
    }

    if (!isEdit && !file) {
      Toast({ icon: 'error', title: 'Selecciona un archivo de video' })
      return
    }

    const payload = {
      video: file || undefined,
      pdf: pdfFile || undefined,
      videoId: video?.id,
      courseId,
      title: title.trim(),
      description: description.trim(),
      position: position.toString().trim(),
      previewSeconds: previewSeconds.toString().trim(),
      previewStart: previewStart.toString().trim(),
      previewHeight: previewHeight.toString().trim(),
    }

    if (isEdit) update(payload)
    else upload(payload)
  }

  return (
    <Modal
      modalId={VIDEO_FORM_MODAL_ID}
      title={isEdit ? 'Editar video' : 'Subir video'}
      titleClassName="fw-bolder text-on-bg-primary"
      modalSize="modal-lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="row g-3 mb-3">
          <div className="col-md-8">
            <label
              className="form-label text-on-bg-primary fw-bold"
              style={{ fontSize: '13px' }}
            >
              Título *
            </label>
            <input
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título del video"
              required
            />
          </div>
          <div className="col-md-4">
            <label
              className="form-label text-on-bg-primary fw-bold"
              style={{ fontSize: '13px' }}
            >
              Posición *
            </label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="1"
              required
            />
          </div>
        </div>

        <div className="mb-3">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            Descripción
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descripción del video"
          />
        </div>

        <div className="mb-4">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            Archivo de video {isEdit ? '(opcional)' : '*'}
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            accept="video/*"
            onChange={handleFileChange}
          />
          {isEdit && (
            <p
              className="mb-0 mt-1"
              style={{ fontSize: '12px', opacity: 0.7 }}
            >
              Deja vacío para conservar el video actual.
            </p>
          )}
          {file && (
            <p
              className="mb-0 mt-1"
              style={{ fontSize: '12px', color: '#166534' }}
            >
              Archivo seleccionado: {file.name}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            PDF de la clase (opcional)
          </label>
          <input
            ref={pdfInputRef}
            type="file"
            className="form-control"
            accept="application/pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
          />
          {isEdit && video?.pdf && (
            <p
              className="mb-0 mt-1"
              style={{ fontSize: '12px', opacity: 0.7 }}
            >
              Deja vacío para conservar el PDF actual.
            </p>
          )}
          {pdfFile && (
            <p
              className="mb-0 mt-1"
              style={{ fontSize: '12px', color: '#166534' }}
            >
              Archivo seleccionado: {pdfFile.name}
            </p>
          )}
        </div>

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <label
              className="form-label text-on-bg-primary fw-bold"
              style={{ fontSize: '13px' }}
            >
              Inicio de vista previa (seg)
            </label>
            <input
              type="text"
              className="form-control"
              value={previewStart}
              onChange={(e) => setPreviewStart(e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="col-md-4">
            <label
              className="form-label text-on-bg-primary fw-bold"
              style={{ fontSize: '13px' }}
            >
              Duración de vista previa (seg)
            </label>
            <input
              type="text"
              className="form-control"
              value={previewSeconds}
              onChange={(e) => setPreviewSeconds(e.target.value)}
              placeholder="5-10"
            />
          </div>
          <div className="col-md-4">
            <label
              className="form-label text-on-bg-primary fw-bold"
              style={{ fontSize: '13px' }}
            >
              Alto de vista previa (px)
            </label>
            <input
              type="text"
              className="form-control"
              value={previewHeight}
              onChange={(e) => setPreviewHeight(e.target.value)}
              placeholder="Se completa al subir el video"
            />
          </div>
          <p className="mb-0" style={{ fontSize: '12px', opacity: 0.7 }}>
            Estos valores se pre-rellenan automáticamente al seleccionar el
            archivo de video y pueden ajustarse manualmente.
          </p>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-white border"
            data-bs-dismiss="modal"
            onClick={() => {
              resetForm()
              onClose()
            }}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-bg-CTA-primary d-flex align-items-center justify-content-center gap-2"
            disabled={isLoading}
            style={{ minWidth: '140px' }}
          >
            {isLoading ? <Loader /> : isEdit ? 'Guardar cambios' : 'Subir video'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default VideoFormModal
